import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft, ScanText, Wand2 } from 'lucide-react';
import { api, ErroApi } from '../../api';
import { pode, useSessao } from '../../sessao';
import { NOME_PECAS, NOME_PRIORIDADE_SLA, NOME_STATUS_CONTRATO, NOME_TIPO_CONTRATO, dataBr, dinheiro, paraLinha, paraMinutos, prazoTexto, type LinhaSla, type Padroes, type Unidade } from './comum';
import { PlanoContrato, type PlanoFicha } from './PlanoContrato';
import { LocadosContrato, type Locado } from './LocadosContrato';
import { Documentos } from './Documentos';

interface Detalhe {
  id: string; numero: string; tipo: string; status: string; origem: string; inicio: string; fim: string | null; janela: string;
  preventivas_mes: string | null; exclusoes: string | null; pecas: string | null; pecas_detalhe: string | null; escopo: string | null; garantia_ate: string | null; dia_vencimento: number | null; observacoes: string | null;
  valor_mensal: string | null; cliente_id: string; cliente: string; versao: number; campos_do_erp: string[];
  sla: { prioridade: string; prazo_atendimento_min: number | null; contagem: string | null }[];
  cobertura: { id: string; categoria_id: string; categoria: string; local_id: string | null; local: string | null; observacao: string | null; inclui_pecas: boolean }[];
  locais: { id: string; nome: string }[]; os: { total: number; abertas: number };
  plano: PlanoFicha; locados: Locado[]; documentos: { id: string; nome_arquivo: string; status: string; enviado_em: string; aplicado_em: string | null }[];
}
interface LinhaSistema { categoria_id: string; marcado: boolean; local_id: string; observacao: string; inclui_pecas: boolean }

export function DetalheContrato() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const podeEditar = pode(usuario, 'contratos.editar');
  const podeAgendar = pode(usuario, 'os.agendar');
  const podeParque = podeEditar && pode(usuario, 'cadastro.editar');
  const [k, setK] = useState<Detalhe | null>(null);
  const [pad, setPad] = useState<Padroes | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  // formulário do Anexo I
  const [f, setF] = useState({ tipo: '', janela: 'comercial', garantia_ate: '', exclusoes: '', observacoes: '', pecas: '', pecas_detalhe: '', escopo: '' });
  const [sla, setSla] = useState<LinhaSla[]>([]);
  const [sist, setSist] = useState<LinhaSistema[]>([]);
  const [fonteSla, setFonteSla] = useState<string[]>([]);

  const montar = useCallback((d: Detalhe, p: Padroes) => {
    setF({ tipo: d.tipo, janela: d.janela, garantia_ate: d.garantia_ate ?? '', exclusoes: d.exclusoes ?? '', observacoes: d.observacoes ?? '',
      pecas: d.pecas ?? '', pecas_detalhe: d.pecas_detalhe ?? '', escopo: d.escopo ?? '' });
    setSla(d.sla.map((s) => paraLinha(s.prioridade, s.prazo_atendimento_min, s.contagem, p.jornada_comercial_min)));
    setSist(p.categorias.map((c) => {
      const x = d.cobertura.find((y) => y.categoria_id === c.id);
      return { categoria_id: c.id, marcado: Boolean(x), local_id: x?.local_id ?? '', observacao: x?.observacao ?? '', inclui_pecas: x?.inclui_pecas ?? false };
    }));
    setFonteSla([]);
  }, []);
  const carregar = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([api<Detalhe>(`/contratos/${id}`), api<Padroes>('/contratos/padroes')]);
      setK(d); setPad(p); montar(d, p);
    } catch (e) { setErro(e instanceof ErroApi && e.status === 404 ? 'Contrato não encontrado.' : (e as Error).message); }
  }, [id, montar]);
  useEffect(() => { carregar(); }, [carregar]);

  if (erro && !k) return <><Link to="/contratos" className="voltar"><ArrowLeft size={16} /> Contratos</Link><p className="alerta">{erro}</p></>;
  if (!k || !pad) return <p className="nota">Carregando…</p>;

  function aplicarPadrao() {
    const p = pad!.tipos[f.tipo];
    if (!p) return;
    const fim = (meses: number) => { const d = new Date(`${k!.inicio}T12:00:00`); d.setMonth(d.getMonth() + meses); return d.toISOString().slice(0, 10); };
    setF({ ...f, janela: p.janela, exclusoes: p.exclusoes ?? '',
      garantia_ate: p.garantia_meses ? fim(p.garantia_meses) : f.garantia_ate });
    setSla(['critica', 'alta', 'media', 'baixa'].map((pr) => {
      const s = p.sla.find((x) => x.prioridade === pr);
      return paraLinha(pr, s?.prazo_atendimento_min ?? null, s?.contagem ?? null, pad!.jornada_comercial_min);
    }));
    setSist(pad!.categorias.map((c) => {
      const s = p.sistemas.find((x) => x.categoria_id === c.id);
      return { categoria_id: c.id, marcado: Boolean(s), local_id: '', observacao: s?.observacao ?? '', inclui_pecas: false };
    }));
    setFonteSla([...new Set(p.sla.map((s) => s.fonte))]);
    setOk(null);
  }

  async function salvar() {
    setErro(null); setOk(null); setSalvando(true);
    try {
      const corpo: Record<string, unknown> = {
        versao: k!.versao, tipo: f.tipo, janela: f.janela, exclusoes: f.exclusoes.trim() || null, observacoes: f.observacoes.trim() || null,
        garantia_ate: f.garantia_ate || null, pecas: f.pecas || null, pecas_detalhe: f.pecas_detalhe.trim() || null, escopo: f.escopo.trim() || null,
      };
      await api(`/contratos/${k!.id}`, { metodo: 'PATCH', corpo });
      await api(`/contratos/${k!.id}/sla`, { metodo: 'PUT', corpo: { itens: sla.map((l) => ({ prioridade: l.prioridade, ...paraMinutos(l, pad!.jornada_comercial_min) })) } });
      await api(`/contratos/${k!.id}/cobertura`, { metodo: 'PUT', corpo: { itens: sist.filter((s) => s.marcado).map((s) => ({ categoria_id: s.categoria_id, local_id: s.local_id || null, observacao: s.observacao.trim() || null, inclui_pecas: s.inclui_pecas })) } });
      setOk('Contrato salvo. As próximas OS deste cliente já usam este SLA.');
      await carregar();
    } catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível salvar.'); } finally { setSalvando(false); }
  }

  const doErp = k.origem === 'conta_azul';
  const garantia = f.tipo.startsWith('garantia');
  const nomeCat = (cid: string) => pad.categorias.find((c) => c.id === cid)?.nome ?? '';

  return (
    <>
      <Link to="/contratos" className="voltar"><ArrowLeft size={16} aria-hidden /> Contratos</Link>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">{NOME_TIPO_CONTRATO[k.tipo]}</span>
          <h1 className="titulo">{k.cliente}</h1>
          <div className="meta">
            <span className={`situacao ${k.status === 'ativo' ? 's-ok' : k.status === 'suspenso' ? 's-nok' : 's-na'}`}>{NOME_STATUS_CONTRATO[k.status]}</span>
            <span className="mono">{k.numero}</span>
            {doErp && <span className="origem-erp" title="Parte comercial vem do Conta Azul">Conta Azul</span>}
            <Link to={`/clientes/${k.cliente_id}`}>ficha do cliente</Link>
          </div>
        </div>
      </div>

      <div className="grade-ficha">
        <section className="cartao ficha">
          <h2>Comercial</h2>
          <dl>
            <div><dt>Vigência</dt><dd>{dataBr(k.inicio)} → {k.fim ? dataBr(k.fim) : 'indeterminado'}</dd></div>
            <div><dt>Situação</dt><dd>{NOME_STATUS_CONTRATO[k.status]}</dd></div>
            {k.valor_mensal != null && <div><dt>Valor mensal</dt><dd>{dinheiro(k.valor_mensal)}</dd></div>}
            <div><dt>OS com este contrato</dt><dd>{k.os.total} ({k.os.abertas} abertas)</dd></div>
          </dl>
          <p className="nota">Vigência, situação, valor, vencimento e reajuste vêm do Conta Azul: corrija lá e a próxima sincronização (30 min) traz.{!doErp && ' Este contrato foi criado no Ops antes dessa regra e não está ligado ao Conta Azul.'}</p>
        </section>
        <section className="cartao ficha">
          <h2>Como o SLA é contado</h2>
          <p className="nota"><strong>Horas corridas</strong> contam o relógio inteiro quando a janela é 24 h. <strong>Horas/dias úteis</strong> contam só seg. a sex., {pad.comercial.comercial_inicio}–{pad.comercial.comercial_fim} (1 dia útil = {pad.jornada_comercial_min / 60} h). Com janela comercial, até as horas corridas só correm no horário comercial.</p>
          <p className="nota">As minutas medem só o <strong>prazo de chegada</strong> (check-in do técnico). Prioridade <strong>crítica</strong> = emergencial: o que compromete o acesso de moradores ou a segurança.</p>
        </section>
      </div>

      <PlanoContrato contratoId={k.id} plano={k.plano} locais={k.locais} podeEditar={podeEditar} podeAgendar={podeAgendar} classificado={k.tipo !== 'a_classificar'}
        aoMudar={(m) => { setOk(m ?? null); carregar(); }} />

      <section className="secao">
        <div className="secao-topo">
          <h2>Anexo I — o que a MAZA cobre</h2>
          {podeEditar && (
            <div className="botoes">
              <button className="btn-fantasma" onClick={aplicarPadrao} disabled={f.tipo === 'a_classificar'} title="Preenche com o padrão das minutas MAZA para este tipo"><Wand2 size={16} aria-hidden /> Padrão do tipo</button>
              <button className="btn" onClick={salvar} disabled={salvando || f.tipo === 'a_classificar'}>{salvando ? 'Salvando…' : 'Salvar contrato'}</button>
            </div>
          )}
        </div>
        {erro && <p className="alerta" role="alert">{erro}</p>}
        {ok && <p className="alerta alerta-ok" role="status">{ok}</p>}
        {f.tipo === 'a_classificar' && <p className="alerta alerta-aviso">Este contrato veio do Conta Azul sem tipo. Escolha o tipo (manutenção, locação, monitoramento…) e use "Padrão do tipo" para preencher.</p>}

        <div className="cartao form anexo">
          <div className="linha-3">
            <label>Tipo
              <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} disabled={!podeEditar}>
                {Object.entries(NOME_TIPO_CONTRATO).map(([v, r]) => <option key={v} value={v}>{r}</option>)}
              </select>
            </label>
            <label>Modelo (janela de atendimento)
              <select value={f.janela} onChange={(e) => setF({ ...f, janela: e.target.value })} disabled={!podeEditar}>
                <option value="24h">24 horas</option><option value="comercial">Horário comercial</option>
              </select>
            </label>
            {garantia ? (
              <label>Garantia até<input type="date" value={f.garantia_ate} onChange={(e) => setF({ ...f, garantia_ate: e.target.value })} disabled={!podeEditar} /></label>
            ) : (
              <label>Peças<select value={f.pecas} onChange={(e) => setF({ ...f, pecas: e.target.value })} disabled={!podeEditar}>
                <option value="">Não informado</option>{Object.entries(NOME_PECAS).map(([v, r]) => <option key={v} value={v}>{r}</option>)}</select></label>
            )}
          </div>

          <h3 className="subtitulo">SLA — prazo máximo para chegada</h3>
          <div className="rolagem-x"><table className="tabela tabela-sla">
            <tbody>
              {sla.map((l, i) => (
                <tr key={l.prioridade}>
                  <th>{NOME_PRIORIDADE_SLA[l.prioridade]}</th>
                  <td><input inputMode="decimal" value={l.valor} placeholder="sem prazo" disabled={!podeEditar}
                    onChange={(e) => setSla(sla.map((x, j) => (j === i ? { ...x, valor: e.target.value } : x)))} aria-label={`Prazo ${l.prioridade}`} /></td>
                  <td><select value={l.unidade} disabled={!podeEditar} onChange={(e) => setSla(sla.map((x, j) => (j === i ? { ...x, unidade: e.target.value as Unidade } : x)))} aria-label={`Unidade ${l.prioridade}`}>
                    <option value="h_corridas">horas corridas</option><option value="h_uteis">horas úteis</option><option value="d_uteis">dias úteis</option>
                  </select></td>
                  <td className="nota">{prazoTexto(paraMinutos(l, pad.jornada_comercial_min).prazo_atendimento_min, paraMinutos(l, pad.jornada_comercial_min).contagem, pad.jornada_comercial_min)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
          {fonteSla.length > 0 && <small className="ajuda">Fonte: {fonteSla.join(' · ')}. Prioridade sem prazo = a minuta não define.</small>}

          <h3 className="subtitulo">Sistemas cobertos</h3>
          <div className="lista-sistemas">
            {sist.map((s, i) => (
              <div key={s.categoria_id} className={`sistema${s.marcado ? ' marcado' : ''}`}>
                <label className="check"><input type="checkbox" checked={s.marcado} disabled={!podeEditar} onChange={(e) => setSist(sist.map((x, j) => (j === i ? { ...x, marcado: e.target.checked } : x)))} /> {nomeCat(s.categoria_id)}</label>
                {s.marcado && (
                  <>
                    <select value={s.local_id} disabled={!podeEditar} onChange={(e) => setSist(sist.map((x, j) => (j === i ? { ...x, local_id: e.target.value } : x)))} aria-label={`Local de ${nomeCat(s.categoria_id)}`}>
                      <option value="">Todos os parques</option>
                      {k.locais.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
                    </select>
                    <input value={s.observacao} disabled={!podeEditar} placeholder="Exceções (ex.: exceto serralheria)" maxLength={300}
                      onChange={(e) => setSist(sist.map((x, j) => (j === i ? { ...x, observacao: e.target.value } : x)))} aria-label={`Exceções de ${nomeCat(s.categoria_id)}`} />
                    <label className="check"><input type="checkbox" checked={s.inclui_pecas} disabled={!podeEditar} onChange={(e) => setSist(sist.map((x, j) => (j === i ? { ...x, inclui_pecas: e.target.checked } : x)))} /> peças inclusas</label>
                  </>
                )}
              </div>
            ))}
          </div>

          {f.pecas && f.pecas !== 'nao_inclui' && <label>Detalhe das peças<input value={f.pecas_detalhe} onChange={(e) => setF({ ...f, pecas_detalhe: e.target.value })} placeholder="ex.: franquia de R$ 300/mês; exceto baterias" maxLength={2000} disabled={!podeEditar} /></label>}
          <label>Serviços previstos (escopo)
            <textarea rows={4} value={f.escopo} onChange={(e) => setF({ ...f, escopo: e.target.value })} disabled={!podeEditar} placeholder="Como está no contrato: o que a MAZA faz nas visitas e nos chamados" />
          </label>
          <label>Fora do contrato (vira orçamento)
            <textarea rows={6} value={f.exclusoes} onChange={(e) => setF({ ...f, exclusoes: e.target.value })} disabled={!podeEditar} />
          </label>
          <label>Observações<textarea rows={2} value={f.observacoes} onChange={(e) => setF({ ...f, observacoes: e.target.value })} disabled={!podeEditar} /></label>
        </div>
      </section>

      <LocadosContrato itens={k.locados} locais={k.locais} podeEditar={podeParque} aoMudar={(m) => { setOk(m ?? null); carregar(); }} />

      <section className="secao">
        <div className="secao-topo"><h2 className="com-icone"><ScanText size={18} aria-hidden /> Contrato escaneado</h2><Link to="/contratos/documentos">todos os escaneados</Link></div>
        <Documentos contratoId={k.id} aoMudar={carregar} />
        {k.documentos.length === 0 && <p className="nota">Nenhum PDF ligado a este contrato ainda.</p>}
      </section>
    </>
  );
}
